/**
 * The command pipeline.
 *
 * VALIDATE FIRST, THEN EXECUTE INFALLIBLY. That split is the whole design.
 *
 * `validate` is pure: it touches nothing and returns either a Plan — everything the
 * change needs, already worked out — or the reason it cannot happen. `execute` takes
 * a Plan and applies it, and cannot fail, because every precondition was checked.
 *
 * Three things follow, and all three are worth more than they cost:
 *
 *   1. A refused command cannot leave the state half-changed. The tile planes are
 *      typed arrays and the state is mutated in place, so without this split the only
 *      alternative would be cloning the world before every click.
 *   2. The interface can ask "may I?" and get a reason back, instead of keeping its
 *      own copy of the rules to decide what to grey out. Duplicated rules are the
 *      most reliable source of bugs in a game this size.
 *   3. From Phase 5 the AI can test legality without speculatively cloning state.
 */

import { traitSum } from '../civs/traits.js';
import { resourceAccess, resourceBlocker, resourcesAt } from '../resources/access.js';
import type { Content } from '../../../content/schema/index.js';
import { tileCount, tileIndex } from '../state/grid.js';
import {
  NONE,
  VIS_VISIBLE,
  type City,
  type Civ,
  type DealItem,
  type GameState,
  type Id,
  type Offer,
  type Payment,
  type ProductionItem,
  type Unit,
  type UnitOrder,
} from '../state/types.js';
import { findPath, wrappedHexDistance } from '../path/astar.js';
import { carrierAt, isBased, inAirRange, isAircraft, planStrike, rollAntiAir, rollInterception, strikeMatchup, type StrikePlan } from '../combat/air.js';
import { roundChance } from '../combat/index.js';
import { indexToOffset } from '../state/grid.js';
import { canAfford, canEnter, IMPASSABLE, moveCost } from '../path/movement.js';
import { beginTurn, runTurnEnd } from '../turn/index.js';
import { automateWorker } from '../ai/index.js';
import { cargoOf, maxHp, rankOf, spareCapacity, unitDef, unitIndex, unitsAtIndex } from '../units/index.js';
import { upgradeCost, upgradeTarget } from '../units/upgrade.js';
import { feelsZoc, zocBlocks, zocPlane } from '../units/zoc.js';
import { chance } from '../rng/index.js';
import { barrage, canAttack, canDefend, fight, fireBarrage, matchup, type Barrage, type Matchup } from '../combat/index.js';
import { captureCity, captureUnits, checkDefeat } from '../combat/conquest.js';
import {
  atWar,
  noteOffer,
  offerCooldown,
  recordLoss,
  relationOf,
  truceLeft,
} from '../diplomacy/index.js';
import { advanceEscort, advanceGoto, applyMove, foreignPresence, isNeighbour, isPort, pathOptions } from '../units/move.js';
import { canBuildImprovements, improvementAllowed } from '../units/work.js';
import { assignWorkedTiles, canWorkTile, cityAt, cityIndex, createCity } from '../cities/index.js';
import { buildBlocker, itemName, placeAllows, switchCost, techBlocker } from '../cities/production.js';
import { wonderBlocker } from '../cities/wonders.js';
import { checkVictory } from '../score.js';
import { changeBlocker, maxScienceRate, startRevolution } from '../government/index.js';
import { mayEnter } from '../diplomacy/borders.js';
import { appraise, dealBlocker, executeDeal, goToWar, refuseDeal, type Appraisal } from '../diplomacy/deals.js';
import { appraisePeace, executePeace, peaceBlocker, termsStillValid } from '../diplomacy/peace.js';
import { damageReputation } from '../diplomacy/memory.js';
import { ensureRelation as relationFor } from '../diplomacy/relation.js';
import { hasTech } from '../research/knowledge.js';
import { recomputeVision } from '../vision/index.js';
import { canResearch, knows, pathTo } from '../research/index.js';
import { wrappedDistance } from '../state/start.js';
import { fail, ok, type Command, type CommandError, type GameEvent, type Result } from './types.js';

export * from './types.js';

/** Everything a command needs in order to be carried out, computed during validation. */
type Plan =
  | { t: 'MoveUnit'; unit: Unit; to: number; cost: number }
  | { t: 'Attack'; unit: Unit; matchup: Matchup; at: number }
  | { t: 'Capture'; unit: Unit; to: number; cost: number; captives: Unit[]; city: City | null }
  | { t: 'DeclareWar'; by: Id; on: Id }
  | { t: 'Board'; unit: Unit; ship: Unit }
  | { t: 'Unload'; unit: Unit }
  | { t: 'Upgrade'; unit: Unit; civ: Civ; to: number; cost: number }
  | { t: 'Bombard'; unit: Unit; barrage: Barrage; at: number }
  | { t: 'Strike'; plan: StrikePlan }
  | { t: 'Rebase'; unit: Unit; to: number; carrier: Unit | null }
  | { t: 'Escort'; unit: Unit; target: Unit }
  /** `accepted` is the AI's answer, decided now; null when a player must answer later. */
  | { t: 'ProposePeace'; from: Id; to: Id; accepted: boolean | null; give: DealItem[]; get: DealItem[] }
  | { t: 'AnswerPeace'; civ: Id; from: Id; accept: boolean }
  | { t: 'SetOrder'; unit: Unit; order: UnitOrder }
  | { t: 'ClearOrder'; unit: Unit }
  | { t: 'SkipUnit'; unit: Unit }
  | { t: 'Automate'; unit: Unit }
  | { t: 'FoundCity'; unit: Unit; at: number }
  | { t: 'SetQueue'; city: City; queue: ProductionItem[]; name: string; lose: number }
  | { t: 'ToggleTile'; city: City; tile: number; working: boolean }
  | { t: 'AutoArrange'; city: City }
  | { t: 'SetSpecialist'; city: City; specialist: number }
  | { t: 'SetResearch'; civ: Civ; tech: number; goal: number }
  | { t: 'SetRate'; civ: Civ; rate: number }
  | { t: 'ChangeGovernment'; civ: Civ; target: number }
  | { t: 'Deal'; from: Id; to: Id; give: DealItem[]; get: DealItem[]; demand: boolean; verdict: Appraisal | null }
  | { t: 'AnswerOffer'; offer: Offer; accept: boolean }
  | { t: 'CancelPayment'; payment: Payment; by: Id }
  | { t: 'CloseBorders'; by: Id; with: Id }
  | { t: 'EndTurn'; civId: Id };

// ─── validation ────────────────────────────────────────────────────────────────

/**
 * After a fight: the loser dies (with anyone aboard), the winner may be promoted, and
 * both sides see afresh. Shared by attacks and air strikes, so they cannot drift apart.
 */
function settleFight(state: GameState, content: Content, unit: Unit, defender: Unit, attackerWon: boolean): GameEvent[] {
  const events: GameEvent[] = [];
  const loser = attackerWon ? defender : unit;
  const winner = attackerWon ? unit : defender;
  recordLoss(state, loser.owner, winner.owner, unitDef(content, loser).cost);
  // A ship that goes down takes everyone aboard with it.
  const drowned = cargoOf(state, loser);
  for (const passenger of drowned) {
    recordLoss(state, loser.owner, winner.owner, unitDef(content, passenger).cost);
    delete state.units[passenger.id];
  }
  if (drowned.length > 0) events.push({ t: 'CargoLost', shipId: loser.id, owner: loser.owner, count: drowned.length });
  delete state.units[loser.id];

  // Surviving a fight can make a veteran of the winner (ADR-0050).
  const ranks = content.rules.combat.ranks;
  const odds = Math.min(
    1,
    (content.rules.combat.promotionChance[winner.experience] ?? 0) *
      (1 + traitSum(content, state.civs[winner.owner], 'promotionPercent') / 100),
  );
  if (winner.experience < ranks.length - 1 && chance(state.rng['combat']!, odds)) {
    const before = maxHp(content, winner);
    winner.experience++;
    winner.hp += maxHp(content, winner) - before;
    events.push({ t: 'UnitPromoted', unitId: winner.id, owner: winner.owner, rank: rankOf(content, winner).name });
  }
  recomputeVision(state, content, unit.owner);
  recomputeVision(state, content, defender.owner);
  events.push(...checkDefeat(state, content, loser.owner));
  return events;
}

function findUnit(state: GameState, unitId: Id): Result<Unit> {
  const unit = state.units[unitId];
  if (!unit) return fail<Unit>('NoSuchUnit');
  if (unit.owner !== state.meta.activeCiv) return fail<Unit>('NotYourUnit');
  return ok(unit);
}

function findCity(state: GameState, cityId: Id): Result<City> {
  const city = state.cities[cityId];
  if (!city) return fail<City>('NoSuchCity');
  if (city.owner !== state.meta.activeCiv) return fail<City>('NotYourCity');
  return ok(city);
}

/** Whether a queue entry names content that actually exists. */
function itemExists(content: Content, item: ProductionItem): boolean {
  try {
    if (item.kind === 'unit') content.units.get(item.type);
    else content.buildings.get(item.building);
    return true;
  } catch {
    return false;
  }
}

function onMap(state: GameState, index: number): boolean {
  return Number.isInteger(index) && index >= 0 && index < tileCount(state.map);
}

export function validate(state: GameState, content: Content, cmd: Command): Result<Plan> {
  switch (cmd.t) {
    case 'MoveUnit': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;

      if (!onMap(state, cmd.to)) return fail<Plan>('OffMap');
      if (isAircraft(unitDef(content, unit))) return fail<Plan>('AircraftFly');
      const from = unitIndex(state.map, unit);
      if (from === cmd.to) return fail<Plan>('AlreadyThere');
      if (!isNeighbour(state, from, cmd.to)) return fail<Plan>('NotAdjacent');

      const def = unitDef(content, unit);
      const terrainThere = content.terrain.get(state.map.terrain[cmd.to]!);

      // A soldier stepping onto water is boarding a ship — one of its own, with room.
      if (def.domain === 'land' && terrainThere.water) {
        const there = unitsAtIndex(state, cmd.to);
        if (there.some((u) => u.owner !== unit.owner)) return fail<Plan>('WrongDomain');
        const ship = there.find((u) => spareCapacity(state, content, u) > 0);
        if (!ship) return fail<Plan>('NoShip');
        if (unit.movesLeft <= 0) return fail<Plan>('NoMovesLeft');
        return ok<Plan>({ t: 'Board', unit, ship });
      }

      const port = def.domain === 'sea' && isPort(state, unit.owner, cmd.to);
      // At peace, a rival's land is closed without open borders (ADR-0060). Someone
      // standing there is answered below — attacking them is war, not trespass.
      if (!mayEnter(state, unit.owner, cmd.to) && unitsAtIndex(state, cmd.to).every((u) => u.owner === unit.owner) && !cityAt(state, state.map, cmd.to)) {
        return fail<Plan>('ClosedBorders');
      }
      if (!canEnter(content, state.map, cmd.to, def, port)) {
        return fail<Plan>(terrainThere.passable ? 'WrongDomain' : 'Impassable');
      }

      const cost = moveCost(content, state.map, cmd.to, def, port, from);
      if (cost === IMPASSABLE) return fail<Plan>('Impassable');
      if (unit.movesLeft <= 0) return fail<Plan>('NoMovesLeft');
      const zocBound = feelsZoc(def) && unit.aboard === NONE;

      // Someone else's units or city: an attack, a capture, or — at peace — nothing.
      const foreignUnits = unitsAtIndex(state, cmd.to).filter((u) => u.owner !== unit.owner);
      const standing = cityAt(state, state.map, cmd.to);
      const foreignCity = standing && standing.owner !== unit.owner ? standing : null;
      if (foreignUnits.length > 0 || foreignCity) {
        const owners = new Set(foreignUnits.map((u) => u.owner));
        if (foreignCity) owners.add(foreignCity.owner);
        for (const owner of owners) if (!atWar(state, unit.owner, owner)) return fail<Plan>('AtPeace');

        const fightHere = matchup(state, content, unit, cmd.to);
        if (fightHere) {
          if (!canAttack(def)) return fail<Plan>('CannotAttack');
          if (unit.aboard !== NONE) return fail<Plan>('FromShip');
          return ok<Plan>({ t: 'Attack', unit, matchup: fightHere, at: cmd.to });
        }
        // Nothing there can defend: walk in and take it, if this unit is a soldier.
        if (!canAttack(def)) return fail<Plan>('CannotCapture');
        if (!canAfford(content, unit.movesLeft, cost)) return fail<Plan>('NoMovesLeft');
        if (zocBound && zocBlocks(state, content, unit.owner, zocPlane(state, content, unit.owner), from, cmd.to)) {
          return fail<Plan>('ZoneOfControl');
        }
        return ok<Plan>({ t: 'Capture', unit, to: cmd.to, cost, captives: foreignUnits, city: foreignCity });
      }

      if (!canAfford(content, unit.movesLeft, cost)) return fail<Plan>('NoMovesLeft');
      if (zocBound && zocBlocks(state, content, unit.owner, zocPlane(state, content, unit.owner), from, cmd.to)) {
        return fail<Plan>('ZoneOfControl');
      }

      return ok<Plan>({ t: 'MoveUnit', unit, to: cmd.to, cost });
    }

    case 'SetGoto': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;

      if (!onMap(state, cmd.to)) return fail<Plan>('OffMap');
      if (isAircraft(unitDef(content, unit))) return fail<Plan>('AircraftFly');
      const from = unitIndex(state.map, unit);
      if (from === cmd.to) return fail<Plan>('AlreadyThere');

      const vision = state.vision[unit.owner] ?? null;
      if (!mayEnter(state, unit.owner, cmd.to) && !foreignPresence(state, unit.owner, cmd.to, vision)) return fail<Plan>('ClosedBorders');
      const route = findPath(pathOptions(state, content, unit, cmd.to), from, cmd.to);
      if (!route || route.length === 0) return fail<Plan>('NoPath');
      // Sent to a tile someone else is visibly holding: go as far as next to it. A goto
      // never attacks — fighting is always a decision made one step at a time.
      const path = foreignPresence(state, unit.owner, cmd.to, vision) ? route.slice(0, -1) : route;
      if (path.length === 0) return fail<Plan>('Occupied');

      return ok<Plan>({
        t: 'SetOrder',
        unit,
        order: { kind: 'goto', path, step: 0, destination: cmd.to },
      });
    }

    case 'BuildImprovement': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;

      if (!canBuildImprovements(content, unit)) return fail<Plan>('CannotBuild');

      let improvement;
      try {
        improvement = content.improvements.get(cmd.improvement);
      } catch {
        return fail<Plan>('NoSuchItem');
      }

      const at = unitIndex(state.map, unit);
      if (improvement.route ? state.map.road[at] === 1 : state.map.improvement[at] === improvement.id) {
        return fail<Plan>('AlreadyImproved');
      }
      // Already on it: asking again must not throw away the work done so far.
      if (unit.orders?.kind === 'build' && unit.orders.improvement === improvement.id) return fail<Plan>('AlreadyWorking');
      if (!improvementAllowed(content, state.map, at, improvement)) return fail<Plan>('WrongGround');
      const owner = state.civs[unit.owner];
      if (owner && !hasTech(owner, improvement.techId)) return fail<Plan>('NeedsTech');

      return ok<Plan>({ t: 'SetOrder', unit, order: { kind: 'build', improvement: improvement.id, progress: 0 } });
    }

    case 'Fortify': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      return ok<Plan>({ t: 'SetOrder', unit: found.value, order: { kind: 'fortify' } });
    }

    case 'Sleep': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      return ok<Plan>({ t: 'SetOrder', unit: found.value, order: { kind: 'sleep' } });
    }

    case 'CancelOrders': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      return ok<Plan>({ t: 'ClearOrder', unit: found.value });
    }

    case 'Automate': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      if (!canBuildImprovements(content, found.value)) return fail<Plan>('CannotBuild');
      return ok<Plan>({ t: 'Automate', unit: found.value });
    }

    case 'SkipUnit': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      return ok<Plan>({ t: 'SkipUnit', unit: found.value });
    }

    case 'FoundCity': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;

      const def = unitDef(content, unit);
      if (!def.abilities.includes('found_city')) return fail<Plan>('CannotFound');
      if (unit.movesLeft <= 0) return fail<Plan>('NoMovesLeft');

      const at = unitIndex(state.map, unit);
      const terrain = content.terrain.get(state.map.terrain[at]!);
      if (terrain.water || !terrain.passable) return fail<Plan>('NotHabitable');
      if (cityAt(state, state.map, at)) return fail<Plan>('CityHere');
      const holder = state.map.owner[at]!;
      if (holder !== NONE && holder !== unit.owner) return fail<Plan>('ForeignTerritory');

      // Spacing keeps cities from being packed one tile apart, which would make
      // expansion a matter of clicking rather than of choosing ground.
      const spacing = content.rules.cities.minSpacing;
      for (const city of Object.values(state.cities)) {
        if (wrappedDistance(state.map, at, cityIndex(state.map, city)) < spacing) {
          return fail<Plan>('CityTooClose');
        }
      }

      return ok<Plan>({ t: 'FoundCity', unit, at });
    }

    case 'SetProduction':
    case 'QueueItem': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      const city = found.value;

      if (!itemExists(content, cmd.item)) return fail<Plan>('NoSuchItem');
      if (cmd.item.kind === 'building' && content.buildings.get(cmd.item.building).effects.isSeatOfGovernment) {
        return fail<Plan>('NotBuildable');
      }
      const owner = state.civs[city.owner];
      const locked = owner ? techBlocker(content, owner, cmd.item) : null;
      if (locked) return fail<Plan>(locked);
      if (!placeAllows(content, state.map, city, cmd.item)) return fail<Plan>('NotCoastal');
      if (resourceBlocker(state, content, city, cmd.item) !== -1) return fail<Plan>('NeedsResource');
      const blocker = buildBlocker(city, cmd.item);
      // Replacing the queue clears whatever was in it, so a duplicate is only a
      // problem when appending.
      if (blocker === 'AlreadyBuilt') return fail<Plan>('AlreadyBuilt');
      if (blocker === 'AlreadyQueued' && cmd.t === 'QueueItem') return fail<Plan>('AlreadyQueued');
      const wonder = wonderBlocker(state, content, city, cmd.item);
      if (wonder) return fail<Plan>(wonder);

      const queue = cmd.t === 'SetProduction' ? [cmd.item] : [...city.queue, cmd.item];
      // Appending to the back of the queue changes nothing about what is being built,
      // so it never costs anything. Only replacing the head does.
      const lose = cmd.t === 'SetProduction' ? switchCost(content, city, cmd.item) : 0;
      return ok<Plan>({ t: 'SetQueue', city, queue, name: itemName(content, cmd.item), lose });
    }

    case 'MoveQueueItem': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      const city = found.value;

      const last = city.queue.length - 1;
      const inRange = (n: number): boolean => Number.isInteger(n) && n >= 0 && n <= last;
      if (!inRange(cmd.from) || !inRange(cmd.to)) return fail<Plan>('NothingQueued');
      if (cmd.from === cmd.to) return fail<Plan>('NothingQueued');

      const queue = [...city.queue];
      const [moved] = queue.splice(cmd.from, 1);
      queue.splice(cmd.to, 0, moved!);

      // Only a change at the head costs anything — reordering behind it is free.
      const lose = queue[0] === city.queue[0] ? 0 : switchCost(content, city, queue[0] ?? null);
      return ok<Plan>({ t: 'SetQueue', city, queue, name: '', lose });
    }

    case 'DequeueItem': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      const city = found.value;
      if (!Number.isInteger(cmd.position) || cmd.position < 0 || cmd.position >= city.queue.length) {
        return fail<Plan>('NothingQueued');
      }
      const queue = city.queue.filter((_, i) => i !== cmd.position);
      // Removing the head promotes whatever was behind it, which is a switch like any
      // other. Removing anything further down is free.
      const lose = cmd.position === 0 ? switchCost(content, city, queue[0] ?? null) : 0;
      return ok<Plan>({ t: 'SetQueue', city, queue, name: '', lose });
    }

    case 'ToggleWorkedTile': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      const city = found.value;
      if (!onMap(state, cmd.tile)) return fail<Plan>('OffMap');

      const working = city.workedTiles.includes(cmd.tile);
      if (!working) {
        if (!canWorkTile(state, content, city, cmd.tile)) return fail<Plan>('TileNotWorkable');
        // Somebody has to move: a city cannot work more tiles than it has citizens.
        if (city.workedTiles.length >= city.population) return fail<Plan>('NoSpareCitizen');
      }
      return ok<Plan>({ t: 'ToggleTile', city, tile: cmd.tile, working });
    }

    case 'AutoArrange': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      return ok<Plan>({ t: 'AutoArrange', city: found.value });
    }

    case 'SetSpecialist': {
      const found = findCity(state, cmd.cityId);
      if (!found.ok) return fail<Plan>(found.error);
      const valid = cmd.specialist === NONE || (Number.isInteger(cmd.specialist) && cmd.specialist >= 0 && cmd.specialist < content.specialists.all.length);
      if (!valid) return fail<Plan>('NoSuchSpecialist');
      return ok<Plan>({ t: 'SetSpecialist', city: found.value, specialist: cmd.specialist });
    }

    case 'SetResearch': {
      const civ = state.civs[state.meta.activeCiv];
      if (!civ) return fail<Plan>('NotYourTurn');
      if (!techExists(content, cmd.tech)) return fail<Plan>('NoSuchTech');
      if (knows(civ, cmd.tech)) return fail<Plan>('AlreadyKnown');
      if (!canResearch(content, civ, cmd.tech)) return fail<Plan>('TechLocked');
      // Choosing directly is taking the wheel: any goal the civilisation had is dropped.
      return ok<Plan>({ t: 'SetResearch', civ, tech: cmd.tech, goal: NONE });
    }

    case 'SetResearchGoal': {
      const civ = state.civs[state.meta.activeCiv];
      if (!civ) return fail<Plan>('NotYourTurn');
      if (!techExists(content, cmd.tech)) return fail<Plan>('NoSuchTech');
      if (knows(civ, cmd.tech)) return fail<Plan>('AlreadyKnown');
      // A goal that is ready now is simply a choice; one further off starts on the way.
      if (canResearch(content, civ, cmd.tech)) return ok<Plan>({ t: 'SetResearch', civ, tech: cmd.tech, goal: NONE });
      const first = pathTo(content, civ, cmd.tech)[0] ?? NONE;
      return ok<Plan>({ t: 'SetResearch', civ, tech: first, goal: cmd.tech });
    }

    case 'SetScienceRate': {
      const civ = state.civs[state.meta.activeCiv];
      if (!civ) return fail<Plan>('NotYourTurn');
      const rules = content.rules.research;
      if (!Number.isInteger(cmd.rate) || cmd.rate < 0 || cmd.rate > rules.maxRate || cmd.rate % rules.rateStep !== 0) {
        return fail<Plan>('BadRate');
      }
      if (cmd.rate > maxScienceRate(state, content, civ.id)) return fail<Plan>('RateAboveGovernment');
      return ok<Plan>({ t: 'SetRate', civ, rate: cmd.rate });
    }

    case 'ChangeGovernment': {
      const civ = state.civs[state.meta.activeCiv];
      if (!civ) return fail<Plan>('NotYourTurn');
      const refused = changeBlocker(content, civ, cmd.government);
      if (refused) return fail<Plan>(refused);
      return ok<Plan>({ t: 'ChangeGovernment', civ, target: cmd.government });
    }

    case 'Board': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      if (unit.aboard !== NONE) return fail<Plan>('NoShip');
      const here = unitIndex(state.map, unit);
      // An aircraft onto a carrier in port (ADR-0066).
      if (unitDef(content, unit).domain === 'air') {
        const deck = carrierAt(state, content, unit.owner, here);
        return deck ? ok<Plan>({ t: 'Board', unit, ship: deck }) : fail<Plan>('NoShip');
      }
      if (unitDef(content, unit).domain !== 'land') return fail<Plan>('NoShip');
      const ship = unitsAtIndex(state, here).find((u) => u.owner === unit.owner && spareCapacity(state, content, u) > 0);
      if (!ship) return fail<Plan>('NoShip');
      return ok<Plan>({ t: 'Board', unit, ship });
    }

    case 'Unload': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      if (unit.aboard === NONE) return fail<Plan>('NotAboard');
      if (content.terrain.get(state.map.terrain[unitIndex(state.map, unit)]!).water) return fail<Plan>('AtSea');
      return ok<Plan>({ t: 'Unload', unit });
    }

    case 'Bombard': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      const def = unitDef(content, unit);
      if (!def.bombard) return fail<Plan>('CannotBombard');
      if (!Number.isInteger(cmd.target) || cmd.target < 0 || cmd.target >= state.map.width * state.map.height) return fail<Plan>('OffMap');
      if (unit.aboard !== NONE) return fail<Plan>('FromShip');
      if (unit.movesLeft <= 0) return fail<Plan>('NoMovesLeft');
      const from = unitIndex(state.map, unit);
      const distance = wrappedHexDistance(state.map, from, cmd.target);
      if (distance < 1 || distance > def.bombard.range) return fail<Plan>('OutOfRange');
      // Only at what it can see, and only at an enemy it is at war with.
      if (state.vision[unit.owner]?.[cmd.target] !== VIS_VISIBLE) return fail<Plan>('NothingToBombard');
      const others = unitsAtIndex(state, cmd.target).filter((u) => u.owner !== unit.owner);
      if (others.length === 0) return fail<Plan>('NothingToBombard');
      for (const other of others) if (!atWar(state, unit.owner, other.owner)) return fail<Plan>('AtPeace');
      const shot = barrage(state, content, unit, cmd.target);
      if (!shot) return fail<Plan>('NothingToBombard');
      return ok<Plan>({ t: 'Bombard', unit, barrage: shot, at: cmd.target });
    }

    case 'Escort': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      const target = state.units[cmd.target];
      const def = unitDef(content, unit);
      if (!target || target.owner !== unit.owner || target.id === unit.id) return fail<Plan>('CannotEscort');
      if (def.domain !== 'sea' || !canDefend(def) || unitDef(content, target).domain !== 'sea') return fail<Plan>('CannotEscort');
      // No circles: the ship being guarded must not be guarding this one, however indirectly.
      for (let next: Unit | undefined = target, guard = 0; next && guard < 32; guard++) {
        if (next.orders?.kind !== 'escort') break;
        if (next.orders.target === unit.id) return fail<Plan>('CannotEscort');
        next = state.units[next.orders.target];
      }
      return ok<Plan>({ t: 'Escort', unit, target });
    }

    case 'AirMission': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      if (!isAircraft(unitDef(content, unit))) return fail<Plan>('NotAircraft');
      if (!onMap(state, cmd.target)) return fail<Plan>('OffMap');
      if (unit.movesLeft <= 0) return fail<Plan>('NoMovesLeft');
      if (!isBased(state, unit)) return fail<Plan>('NotInCity');
      if (!inAirRange(state, content, unit, cmd.target)) return fail<Plan>('OutOfRange');
      // One of our own cities: fly there and stay. One of our carriers with room: land on it.
      const there = cityAt(state, state.map, cmd.target);
      if (there && there.owner === unit.owner) return ok<Plan>({ t: 'Rebase', unit, to: cmd.target, carrier: null });
      const deck = carrierAt(state, content, unit.owner, cmd.target);
      if (deck) return ok<Plan>({ t: 'Rebase', unit, to: cmd.target, carrier: deck });
      if (state.vision[unit.owner]?.[cmd.target] !== VIS_VISIBLE) return fail<Plan>('NothingToStrike');
      const others = unitsAtIndex(state, cmd.target).filter((u) => u.owner !== unit.owner);
      if (others.length === 0) return fail<Plan>('NothingToStrike');
      for (const other of others) if (!atWar(state, unit.owner, other.owner)) return fail<Plan>('AtPeace');
      const plan = planStrike(state, content, unit, cmd.target);
      if (!plan) return fail<Plan>('NothingToStrike');
      return ok<Plan>({ t: 'Strike', plan });
    }

    case 'UpgradeUnit': {
      const found = findUnit(state, cmd.unitId);
      if (!found.ok) return fail<Plan>(found.error);
      const unit = found.value;
      const civ = state.civs[unit.owner];
      if (!civ) return fail<Plan>('NoSuchCiv');
      const from = unitDef(content, unit);
      if (!upgradeTarget(content, civ, from)) return fail<Plan>('NoUpgrade');
      if (!isPort(state, unit.owner, unitIndex(state.map, unit))) return fail<Plan>('NotInCity');
      // Refitting needs what building would: the strategic resources, in this city (ADR-0063).
      const has = resourcesAt(state, resourceAccess(state, content, unit.owner), unitIndex(state.map, unit));
      const to = upgradeTarget(content, civ, from, has);
      if (!to) return fail<Plan>('NeedsResource');
      if (unit.aboard !== NONE) return fail<Plan>('NotInCity');
      const cost = upgradeCost(content, from, to);
      if (civ.treasury < cost) return fail<Plan>('CannotAfford');
      return ok<Plan>({ t: 'Upgrade', unit, civ, to: to.id, cost });
    }

    case 'DeclareWar': {
      const by = state.meta.activeCiv;
      const target = state.civs[cmd.on];
      if (!target || target.defeated || cmd.on === by) return fail<Plan>('NoSuchCiv');
      if (atWar(state, by, cmd.on)) return fail<Plan>('AlreadyAtWar');
      if (truceLeft(state, by, cmd.on) > 0) return fail<Plan>('Truce');
      return ok<Plan>({ t: 'DeclareWar', by, on: cmd.on });
    }

    case 'ProposePeace': {
      const from = state.meta.activeCiv;
      const target = state.civs[cmd.to];
      if (!target || target.defeated || cmd.to === from) return fail<Plan>('NoSuchCiv');
      if (!atWar(state, from, cmd.to)) return fail<Plan>('NotAtWar');
      if (offerCooldown(state, content, from, cmd.to) > 0) return fail<Plan>('OfferTooSoon');
      if (relationOf(state, from, cmd.to)?.peaceOfferedBy === from) return fail<Plan>('OfferTooSoon');
      const give = cmd.give ?? [];
      const get = cmd.get ?? [];
      const blocked = peaceBlocker(state, content, from, cmd.to, give, get);
      if (blocked) return fail<Plan>(blocked);
      // An AI answers on the spot, weighing the terms against what it is fighting for
      // (ADR-0071) — by the same rule it uses to decide to offer peace itself.
      const accepted = target.isHuman ? null : appraisePeace(state, content, cmd.to, from, give, get).accept;
      return ok<Plan>({ t: 'ProposePeace', from, to: cmd.to, accepted, give, get });
    }

    case 'AnswerPeace': {
      const civ = state.meta.activeCiv;
      if (!state.civs[cmd.from]) return fail<Plan>('NoSuchCiv');
      const pending = relationOf(state, civ, cmd.from);
      if (pending?.peaceOfferedBy !== cmd.from) return fail<Plan>('NoOffer');
      // Terms that can no longer be met — a city lost since, gold spent — can only be refused.
      if (cmd.accept && !termsStillValid(state, content, cmd.from, civ, pending.peaceGive, pending.peaceGet)) return fail<Plan>('BadDealItem');
      return ok<Plan>({ t: 'AnswerPeace', civ, from: cmd.from, accept: cmd.accept });
    }

    case 'ProposeDeal': {
      const from = state.meta.activeCiv;
      const demand = cmd.demand === true;
      if (demand && cmd.give.length > 0) return fail<Plan>('BadDealItem');
      const refused = dealBlocker(state, content, from, cmd.to, cmd.give, cmd.get);
      if (refused) return fail<Plan>(refused);
      // An AI answers on the spot, by the same judgement it uses for its own deals.
      const target = state.civs[cmd.to]!;
      const verdict = target.isHuman ? null : appraise(state, content, cmd.to, from, cmd.give, cmd.get, demand);
      return ok<Plan>({ t: 'Deal', from, to: cmd.to, give: cmd.give, get: cmd.get, demand, verdict });
    }

    case 'AnswerOffer': {
      const offer = state.offers.find((o) => o.id === cmd.offerId);
      if (!offer || offer.to !== state.meta.activeCiv) return fail<Plan>('NoOffer');
      if (cmd.accept && dealBlocker(state, content, offer.from, offer.to, offer.give, offer.get)) return fail<Plan>('OfferStale');
      return ok<Plan>({ t: 'AnswerOffer', offer, accept: cmd.accept });
    }

    case 'CancelAgreement': {
      const by = state.meta.activeCiv;
      if (cmd.payment !== undefined) {
        const payment = state.payments.find((p) => p.id === cmd.payment);
        if (!payment || (payment.from !== by && payment.to !== by)) return fail<Plan>('NoSuchAgreement');
        return ok<Plan>({ t: 'CancelPayment', payment, by });
      }
      if (cmd.openBordersWith !== undefined) {
        if (!relationOf(state, by, cmd.openBordersWith)?.openBorders) return fail<Plan>('NoSuchAgreement');
        return ok<Plan>({ t: 'CloseBorders', by, with: cmd.openBordersWith });
      }
      return fail<Plan>('NoSuchAgreement');
    }

    case 'EndTurn': {
      if (cmd.civId !== state.meta.activeCiv) return fail<Plan>('NotYourTurn');
      return ok<Plan>({ t: 'EndTurn', civId: cmd.civId });
    }
  }
}

function techExists(content: Content, tech: number): boolean {
  return Number.isInteger(tech) && tech >= 0 && tech < content.technologies.all.length;
}

// ─── execution ─────────────────────────────────────────────────────────────────

function execute(state: GameState, content: Content, plan: Plan): GameEvent[] {
  switch (plan.t) {
    case 'Automate': {
      plan.unit.automated = true;
      plan.unit.orders = null;
      return automateWorker(state, content, plan.unit);
    }
    case 'MoveUnit': {
      // A move given by hand overrides whatever the unit was told to do before.
      const events = applyMove(state, content, plan.unit, plan.to, plan.cost);
      if (plan.unit.orders?.kind === 'goto' || plan.unit.orders?.kind === 'escort') {
        plan.unit.orders = null;
        events.push({ t: 'UnitOrderCleared', unitId: plan.unit.id });
      }
      return events;
    }

    case 'Attack': {
      const { unit, matchup: m } = plan;
      const defender = m.defender;
      const result = fight(content, m, state.rng['combat']!);
      const scale = content.rules.movement.scale;
      unit.movesLeft = Math.max(0, unit.movesLeft - content.rules.combat.attackCost * scale);
      unit.orders = null;
      unit.hp = result.attackerHp;
      defender.hp = result.defenderHp;

      const events: GameEvent[] = [
        {
          t: 'CombatResolved',
          attackerId: unit.id,
          defenderId: defender.id,
          attackerType: unit.type,
          defenderType: defender.type,
          attackerOwner: unit.owner,
          defenderOwner: defender.owner,
          at: plan.at,
          attackerWon: result.attackerWon,
          attackerHp: result.attackerHp,
          defenderHp: result.defenderHp,
          odds: m.odds,
        },
      ];

      events.push(...settleFight(state, content, unit, defender, result.attackerWon));
      return events;
    }

    case 'Capture': {
      const by = plan.unit.owner;
      const owners = new Set(plan.captives.map((u) => u.owner));
      const events = captureUnits(state, content, plan.captives, by);
      events.push(...applyMove(state, content, plan.unit, plan.to, plan.cost));
      plan.unit.orders = null;
      if (plan.city) events.push(...captureCity(state, content, plan.city, by, state.rng['combat']!));
      for (const owner of owners) {
        recomputeVision(state, content, owner);
        events.push(...checkDefeat(state, content, owner));
      }
      return events;
    }

    case 'DeclareWar':
      // Declared before a treaty ends, it is broken, and everyone hears (ADR-0060).
      return goToWar(state, content, plan.by, plan.on);

    case 'Deal': {
      const relation = relationFor(state, plan.from, plan.to);
      if (relation.a === plan.from) relation.lastDealA = state.meta.turn;
      else relation.lastDealB = state.meta.turn;
      if (plan.verdict === null) {
        const offer: Offer = {
          id: state.nextId++,
          from: plan.from,
          to: plan.to,
          give: plan.give,
          get: plan.get,
          demand: plan.demand,
          expires: state.meta.turn + content.rules.diplomacy.offerTurns,
        };
        state.offers.push(offer);
        return [{ t: 'DealOffered', offerId: offer.id, from: plan.from, to: plan.to, demand: plan.demand }];
      }
      if (plan.verdict.accept) return executeDeal(state, content, plan.from, plan.to, plan.give, plan.get, plan.demand);
      return refuseDeal(state, plan.to, plan.from, plan.verdict.reason, plan.demand);
    }

    case 'AnswerOffer': {
      const { offer } = plan;
      state.offers = state.offers.filter((o) => o.id !== offer.id);
      if (plan.accept) return executeDeal(state, content, offer.from, offer.to, offer.give, offer.get, offer.demand);
      return refuseDeal(state, offer.to, offer.from, 'Refused', offer.demand);
    }

    case 'CancelPayment': {
      const { payment } = plan;
      state.payments = state.payments.filter((p) => p.id !== payment.id);
      // The payer stopping is a broken promise; the receiver letting it go is not.
      const defaulted = plan.by === payment.from;
      if (defaulted) damageReputation(state, payment.from, content.rules.diplomacy.reputation.defaulted);
      return [{ t: 'PaymentStopped', from: payment.from, to: payment.to, defaulted }];
    }

    case 'CloseBorders': {
      relationFor(state, plan.by, plan.with).openBorders = false;
      return [{ t: 'OpenBordersEnded', by: plan.by, with: plan.with }];
    }

    case 'Board': {
      const { unit, ship } = plan;
      unit.col = ship.col;
      unit.row = ship.row;
      unit.aboard = ship.id;
      unit.movesLeft = 0;
      // Asleep below decks, so it is not offered as waiting for orders at every turn
      // of the voyage. It wakes by being moved ashore.
      unit.orders = { kind: 'sleep' };
      recomputeVision(state, content, unit.owner);
      return [{ t: 'UnitBoarded', unitId: unit.id, shipId: ship.id, at: unitIndex(state.map, ship) }];
    }

    case 'Unload': {
      const shipId = plan.unit.aboard;
      plan.unit.aboard = NONE;
      plan.unit.orders = null;
      return [{ t: 'UnitLanded', unitId: plan.unit.id, shipId, at: unitIndex(state.map, plan.unit) }];
    }

    case 'Bombard': {
      const { unit, barrage: b } = plan;
      const { hits, damage } = fireBarrage(content, b, state.rng['combat']!);
      b.target.hp -= damage;
      // Firing is the unit's turn: guns are laid, fired and limbered up again.
      unit.movesLeft = 0;
      unit.orders = null;
      return [
        {
          t: 'UnitBombarded',
          attackerId: unit.id,
          targetId: b.target.id,
          attackerOwner: unit.owner,
          targetOwner: b.target.owner,
          targetType: b.target.type,
          at: plan.at,
          shots: b.shots,
          hits,
          damage,
          targetHp: b.target.hp,
        },
      ];
    }

    case 'Escort': {
      plan.unit.orders = { kind: 'escort', target: plan.target.id };
      return [{ t: 'EscortSet', unitId: plan.unit.id, target: plan.target.id }, ...advanceEscort(state, content, plan.unit)];
    }

    case 'Rebase': {
      const { unit } = plan;
      const from = unitIndex(state.map, unit);
      const { col, row } = indexToOffset(state.map, plan.to);
      unit.col = col;
      unit.row = row;
      unit.aboard = plan.carrier ? plan.carrier.id : NONE;
      unit.movesLeft = 0;
      unit.orders = null;
      recomputeVision(state, content, unit.owner);
      return [{ t: 'AircraftRebased', unitId: unit.id, owner: unit.owner, from, to: plan.to }];
    }

    case 'Strike': {
      const { striker, at, defender, interceptor, antiAir } = plan.plan;
      const def = unitDef(content, striker);
      const rng = state.rng['combat']!;
      const events: GameEvent[] = [];
      striker.movesLeft = 0;
      striker.orders = null;
      const lose = (cause: 'intercepted' | 'antiAir'): GameEvent[] => {
        recordLoss(state, striker.owner, defender.owner, def.cost);
        delete state.units[striker.id];
        events.push({ t: 'AircraftLost', unitId: striker.id, unitType: striker.type, owner: striker.owner, at, cause });
        recomputeVision(state, content, striker.owner);
        events.push(...checkDefeat(state, content, striker.owner));
        return events;
      };

      // 1. An enemy fighter may meet it on the way.
      if (interceptor) {
        const p = roundChance(unitDef(content, interceptor).attack, def.defence);
        const outcome = rollInterception(content, striker.hp, interceptor.hp, p, rng);
        striker.hp = outcome.strikerHp;
        interceptor.hp = outcome.interceptorHp;
        events.push({
          t: 'AirIntercepted',
          strikerId: striker.id,
          strikerType: striker.type,
          strikerOwner: striker.owner,
          interceptorId: interceptor.id,
          interceptorOwner: interceptor.owner,
          at,
          strikerHp: striker.hp,
          interceptorHp: interceptor.hp,
        });
        if (interceptor.hp <= 0) {
          recordLoss(state, interceptor.owner, striker.owner, unitDef(content, interceptor).cost);
          delete state.units[interceptor.id];
          events.push({ t: 'AircraftLost', unitId: interceptor.id, unitType: interceptor.type, owner: interceptor.owner, at, cause: 'shotDown' });
        }
        if (striker.hp <= 0) return lose('intercepted');
      }

      // 2. The city's guns.
      if (antiAir > 0) {
        const hits = rollAntiAir(content, antiAir, rng);
        const damage = Math.min(striker.hp, hits * content.rules.combat.damagePerRound);
        striker.hp -= damage;
        events.push({ t: 'AntiAirFired', strikerId: striker.id, strikerOwner: striker.owner, cityOwner: defender.owner, at, hits, damage });
        if (striker.hp <= 0) return lose('antiAir');
      }

      // 3. What is left fights the defender, at whatever health it arrives with.
      const result = fight(content, strikeMatchup(state, content, striker, defender, at), rng);
      striker.hp = result.attackerHp;
      defender.hp = result.defenderHp;
      events.push({
        t: 'CombatResolved',
        attackerId: striker.id,
        defenderId: defender.id,
        attackerType: striker.type,
        defenderType: defender.type,
        attackerOwner: striker.owner,
        defenderOwner: defender.owner,
        at,
        attackerWon: result.attackerWon,
        attackerHp: result.attackerHp,
        defenderHp: result.defenderHp,
        odds: plan.plan.odds,
      });
      events.push(...settleFight(state, content, striker, defender, result.attackerWon));
      return events;
    }

    case 'Upgrade': {
      const { unit, civ } = plan;
      const before = maxHp(content, unit);
      const from = unit.type;
      civ.treasury -= plan.cost;
      unit.type = plan.to;
      // Keep the share of health it had; a wounded spearman becomes a wounded pikeman.
      unit.hp = Math.max(1, Math.round((unit.hp / before) * maxHp(content, unit)));
      unit.movesLeft = 0;
      unit.orders = null;
      return [{ t: 'UnitUpgraded', unitId: unit.id, from, to: plan.to, cost: plan.cost }];
    }

    case 'ProposePeace': {
      noteOffer(state, plan.from, plan.to);
      if (plan.accepted === true) return executePeace(state, content, plan.from, plan.to, plan.give, plan.get);
      if (plan.accepted === false) return [{ t: 'PeaceRefused', by: plan.to, to: plan.from }];
      const relation = relationOf(state, plan.from, plan.to);
      if (relation) {
        relation.peaceOfferedBy = plan.from;
        relation.peaceGive = plan.give;
        relation.peaceGet = plan.get;
      }
      return [{ t: 'PeaceOffered', from: plan.from, to: plan.to, give: plan.give, get: plan.get }];
    }

    case 'AnswerPeace': {
      const relation = relationOf(state, plan.civ, plan.from);
      if (plan.accept) return executePeace(state, content, plan.from, plan.civ, relation?.peaceGive ?? [], relation?.peaceGet ?? []);
      if (relation) {
        relation.peaceOfferedBy = NONE;
        relation.peaceGive = [];
        relation.peaceGet = [];
      }
      return [{ t: 'PeaceRefused', by: plan.civ, to: plan.from }];
    }

    case 'SetOrder': {
      plan.unit.orders = plan.order;
      const events: GameEvent[] = [{ t: 'UnitOrderSet', unitId: plan.unit.id, order: plan.order }];
      // A goto given during your own turn should start moving now, not next turn.
      if (plan.order.kind === 'goto') events.push(...advanceGoto(state, content, plan.unit));
      return events;
    }

    case 'ClearOrder':
      plan.unit.orders = null;
      return [{ t: 'UnitOrderCleared', unitId: plan.unit.id }];

    case 'SkipUnit':
      plan.unit.movesLeft = 0;
      return [];

    case 'FoundCity': {
      const owner = plan.unit.owner;
      // The settler is consumed: it becomes the city rather than standing beside it.
      delete state.units[plan.unit.id];
      const city = createCity(state, content, owner, plan.at);
      recomputeVision(state, content, owner);
      return [{ t: 'CityFounded', cityId: city.id, civId: owner, name: city.name, at: plan.at }];
    }

    case 'SetQueue': {
      plan.city.queue = plan.queue;
      const events: GameEvent[] = [];
      if (plan.lose > 0) {
        plan.city.productionStore -= plan.lose;
        events.push({ t: 'ProductionLost', cityId: plan.city.id, amount: plan.lose });
      }
      if (plan.name) events.push({ t: 'ProductionSet', cityId: plan.city.id, name: plan.name });
      return events;
    }

    case 'ToggleTile': {
      const city = plan.city;
      // Touching a tile by hand takes the city off its governor — otherwise the next
      // turn would quietly undo the player's decision.
      city.governed = false;
      if (plan.working) {
        city.workedTiles = city.workedTiles.filter((t) => t !== plan.tile);
        if (state.map.workedBy[plan.tile] === city.id) state.map.workedBy[plan.tile] = NONE;
      } else {
        city.workedTiles = [...city.workedTiles, plan.tile].sort((a, b) => a - b);
        state.map.workedBy[plan.tile] = city.id;
      }
      return [{ t: 'WorkedTilesChanged', cityId: city.id }];
    }

    case 'AutoArrange': {
      plan.city.governed = true;
      assignWorkedTiles(state, content, plan.city);
      return [{ t: 'WorkedTilesChanged', cityId: plan.city.id }];
    }

    case 'SetSpecialist':
      plan.city.specialist = plan.specialist;
      // Auto can move citizens between tiles and entertainment, so let it look again.
      assignWorkedTiles(state, content, plan.city);
      return [{ t: 'WorkedTilesChanged', cityId: plan.city.id }];

    case 'SetResearch':
      plan.civ.researching = plan.tech;
      plan.civ.researchGoal = plan.goal;
      return [{ t: 'ResearchSet', civId: plan.civ.id, tech: plan.tech, goal: plan.goal }];

    case 'SetRate':
      plan.civ.researchRate = plan.rate;
      return [{ t: 'ScienceRateSet', civId: plan.civ.id, rate: plan.rate }];

    case 'ChangeGovernment':
      return startRevolution(state, content, plan.civ, plan.target);

    case 'EndTurn': {
      const events = runTurnEnd(state, content, plan.civId);
      events.push(...beginTurn(state, content));
      return events;
    }
  }
}

// ─── the public entry point ────────────────────────────────────────────────────

/**
 * Validate a command and, if it is legal, carry it out. The signature ARCHITECTURE.md
 * specifies, with `content` alongside the state because content is deliberately not
 * part of a saved game (see ADR-0004).
 */
export function apply(state: GameState, content: Content, cmd: Command): Result<GameEvent[]> {
  const plan = validate(state, content, cmd);
  if (!plan.ok) return fail<GameEvent[]>(plan.error);
  // Any other order given to an automated worker takes it off automation (ADR-0077) —
  // except the orders automation gives it itself.
  if ('unitId' in cmd && cmd.t !== 'Automate' && automationDepth === 0) {
    const unit = state.units[cmd.unitId];
    if (unit) unit.automated = false;
  }
  const events = execute(state, content, plan.value);
  // A civilisation fell: that may have ended the game (ADR-0061).
  if (events.some((e) => e.t === 'CivDefeated')) events.push(...checkVictory(state, content));
  return ok(events);
}

let automationDepth = 0;

/** Run commands on automation's behalf: they do not take a worker off automation. */
export function asAutomation<T>(f: () => T): T {
  automationDepth++;
  try {
    return f();
  } finally {
    automationDepth--;
  }
}

/** Whether a command would be accepted, without carrying it out. */
export function canApply(state: GameState, content: Content, cmd: Command): CommandError | null {
  const plan = validate(state, content, cmd);
  return plan.ok ? null : plan.error;
}

/** Plane index for a unit's tile, re-exported so the shell need not know the layout. */
export function indexOf(state: GameState, col: number, row: number): number {
  return tileIndex(state.map, col, row);
}
